/**
 * Small building blocks for the implementer console: panels, facts, copy buttons, code blocks and calm placeholders
 * for endpoints that are not deployed yet.
 */
import { ReactNode, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, SxProps, Theme, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ArrowLeft, Check, Copy, Hourglass, TerminalWindow } from "@phosphor-icons/react";
import { Mono } from "../ui";
import { caps, fonts, tones } from "../theme";

export const ago = (iso: string | null | undefined) => {
  if (!iso) return "never";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 0) return "just now";
  return s < 90 ? `${s}s ago` : s < 5400 ? `${Math.round(s / 60)}m ago` : s < 129600 ? `${Math.round(s / 3600)}h ago` : `${Math.round(s / 86400)}d ago`;
};

const pad = (n: number, w = 2) => String(n).padStart(w, "0");
/** Local wall-clock time with milliseconds, e.g. 14:03:22.517. */
export const clockMs = (iso: string) => {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};
export const localDate = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** A quiet icon button that copies `value` and confirms with a check for a moment. */
export function CopyButton({ value, label = "Copy", size = 14 }: { value: string; label?: string; size?: number }) {
  const [done, setDone] = useState(false);
  return (
    <Tooltip title={done ? "Copied" : label}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={async () => {
          if (await copyText(value)) {
            setDone(true);
            setTimeout(() => setDone(false), 1400);
          }
        }}
        sx={{ color: done ? tones.ok : "text.secondary", p: 0.5, "&:hover": { color: "text.primary" } }}
      >
        {done ? <Check size={size} /> : <Copy size={size} />}
      </IconButton>
    </Tooltip>
  );
}

/** An identifier in mono with a copy button beside it. */
export function CopyValue({ value, display, sx, big }: { value: string; display?: ReactNode; sx?: SxProps<Theme>; big?: boolean }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, ...sx }}>
      <Mono sx={{ fontSize: big ? 18 : "0.85em", color: big ? "primary.light" : "text.primary", wordBreak: "break-all" }}>{display ?? value}</Mono>
      <CopyButton value={value} size={big ? 16 : 14} />
    </Stack>
  );
}

/** A bordered section with an overline title and optional trailing actions. */
export function Panel({ title, icon, action, children, sx, id }: { title: ReactNode; icon?: ReactNode; action?: ReactNode; children: ReactNode; sx?: SxProps<Theme>; id?: string }) {
  return (
    <Paper id={id} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "14px", minWidth: 0, ...sx }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, minHeight: 28 }}>
        {icon && <Box sx={{ display: "flex", color: "primary.main" }}>{icon}</Box>}
        <Typography variant="overline" sx={{ color: "text.secondary", lineHeight: 1.6, flexGrow: 1 }}>{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

/** A small label over a value. */
export function Fact({ label, children, sx }: { label: ReactNode; children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Typography sx={{ ...caps, fontSize: 9, letterSpacing: "0.18em", color: "text.secondary", mb: 0.25 }}>{label}</Typography>
      <Box sx={{ fontSize: 14, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

/** A terminal-style block with a copy button. */
export function CodeBlock({ text, label }: { text: string; label?: string }) {
  return (
    <Box>
      {label && <Typography sx={{ ...caps, fontSize: 9, letterSpacing: "0.18em", color: "text.secondary", mb: 0.75 }}>{label}</Typography>}
      <Box sx={{ position: "relative" }}>
        <Box sx={{ position: "absolute", left: 12, top: 12, color: "primary.main", display: "flex" }}><TerminalWindow size={15} /></Box>
        <Box sx={{ position: "absolute", right: 6, top: 6 }}><CopyButton value={text} label="Copy command" /></Box>
        <Box
          component="pre"
          sx={{ m: 0, p: 1.5, pl: 4.5, pr: 5, bgcolor: "rgba(0,0,0,0.45)", border: 1, borderColor: "divider", borderRadius: "12px", overflowX: "auto", fontSize: 12.5, lineHeight: 1.6, fontFamily: fonts.mono, color: "#f3e4cf", whiteSpace: "pre-wrap", wordBreak: "break-all" }}
        >
          {text}
        </Box>
      </Box>
    </Box>
  );
}

export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Button component={RouterLink} to={to} size="small" color="inherit" startIcon={<ArrowLeft size={14} />} sx={{ color: "text.secondary", ml: -1.5, alignSelf: "flex-start" }}>
      {label}
    </Button>
  );
}

/** Calm placeholder for a part of the console whose endpoint has not been deployed on this server yet. */
export function NotYet({ children, compact }: { children: ReactNode; compact?: boolean }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: compact ? 1 : 3, px: compact ? 0 : 1, color: "text.secondary" }}>
      <Box sx={{ display: "flex", color: alpha(tones.ochre, 0.7) }}><Hourglass size={20} /></Box>
      <Typography variant="body2">{children}</Typography>
    </Stack>
  );
}

/** A small tag, e.g. "to confirm with vendor". */
export function Tag({ children, color = tones.warn }: { children: ReactNode; color?: string }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", height: 18, px: 0.75, borderRadius: "5px", border: `1px solid ${alpha(color, 0.35)}`, bgcolor: alpha(color, 0.08), color, fontSize: "0.62rem", fontWeight: 500, letterSpacing: "0.04em", whiteSpace: "nowrap", lineHeight: 1 }}>
      {children}
    </Box>
  );
}

/** Confirmation dialog for actions with side effects (restart, regenerate). */
export function Confirm({
  open, title, children, confirm, onClose, onConfirm, busy, danger,
}: { open: boolean; title: string; children: ReactNode; confirm: string; onClose: () => void; onConfirm: () => void; busy?: boolean; danger?: boolean }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent><Stack spacing={1.5}>{children}</Stack></DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={danger ? "error" : "primary"} onClick={onConfirm} disabled={busy}>{confirm}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Format a SHA-256 hex string as colon-separated uppercase pairs. */
export const formatFingerprint = (hex: string) => {
  const clean = hex.replace(/[^0-9a-fA-F]/g, "").toUpperCase();
  return clean.match(/.{1,2}/g)?.join(":") ?? hex;
};

/** SHA-256 fingerprint of the first certificate in a PEM block, computed in the browser. Null when it is not a PEM certificate. */
export async function pemFingerprint(pem: string): Promise<string | null> {
  const m = /-----BEGIN CERTIFICATE-----([\s\S]+?)-----END CERTIFICATE-----/.exec(pem);
  if (!m || !crypto?.subtle) return null;
  try {
    const bin = atob(m[1].replace(/\s+/g, ""));
    const der = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) der[i] = bin.charCodeAt(i);
    const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", der));
    return Array.from(digest, (b) => b.toString(16).padStart(2, "0")).join(":").toUpperCase();
  } catch {
    return null;
  }
}
